/**
 * Datadog Browser RUM for Homestead.
 *
 * Datadog's setup snippet is ~15 lines with hardcoded applicationId, clientToken,
 * service, and env. This file is longer because Homestead is a multi-environment
 * Backstage app, not a single-page demo:
 *
 * - Config-driven creds (app-config + env / Secrets Manager) — never hardcoded in repo
 * - Explicit RUM toggle (homestead.datadog.rum.enabled) and env tag (homestead.datadog.rum.env)
 * - React lifecycle — init runs after ConfigApi is available, not at module load
 * - CFA tag enforcement — org_id and team via setGlobalContextProperty (not init params)
 * - Unified tagging — version from CI build timestamp
 * - RUM ↔ APM correlation — allowedTracingUrls from backend.baseUrl (per deploy)
 * - User attribution — setUser after Okta / guest identity resolves
 * - initialized guard — avoids double init on React strict-mode remounts
 */
import { useEffect, type ReactNode } from 'react';
import type { ConfigApi } from '@backstage/core-plugin-api';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { datadogRum } from '@datadog/browser-rum';
import type { HomesteadE2eRumOverride } from './datadogRumE2e';

const DATADOG_RUM_SERVICE = 'homestead';

export type { HomesteadE2eRumOverride };

declare global {
  interface Window {
    __HOMESTEAD_E2E_RUM__?: HomesteadE2eRumOverride;
  }
}

function getE2eRumOverride(): HomesteadE2eRumOverride | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.__HOMESTEAD_E2E_RUM__;
}

/** Module-level guard; datadogRum.init() must only run once per page load. */
let initialized = false;

/** Resolve deploy version from CI build timestamp, falling back to Backstage version. */
export function resolveDatadogVersion(config: ConfigApi): string {
  return (
    config.getOptionalString('homestead.buildInfo.timestamp') ??
    config.getOptionalString('homestead.buildInfo.version') ??
    'unknown'
  );
}

/** RUM on when homestead.datadog.rum.enabled is true, or when e2e sets window.__HOMESTEAD_E2E_RUM__. */
export function isDatadogRumEnabled(config: ConfigApi): boolean {
  if (getE2eRumOverride()) {
    return true;
  }
  const enabled = config.getOptionalBoolean('homestead.datadog.rum.enabled');
  if (enabled !== undefined) {
    return enabled;
  }
  // Env substitution from shell/Atlas often yields the string "true" / "false".
  return config.getOptionalString('homestead.datadog.rum.enabled') === 'true';
}

/** Datadog `env` tag for RUM sessions (HOMESTEAD_DATADOG_RUM_ENV in app-config). */
export function resolveDatadogRumEnv(config: ConfigApi): string {
  return config.getOptionalString('homestead.datadog.rum.env') ?? 'unknown';
}

/** Trace injection on localhost breaks guest auth (CORS / fetch hooks); skip for local backends. */
export function resolveAllowedTracingUrls(config: ConfigApi): string[] | undefined {
  const backendBaseUrl = config.getOptionalString('backend.baseUrl');
  if (!backendBaseUrl) {
    return undefined;
  }

  try {
    const { hostname } = new URL(backendBaseUrl);
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return undefined;
    }
  } catch {
    return undefined;
  }

  return [backendBaseUrl];
}

function initDatadogRum(config: ConfigApi): void {
  const e2e = getE2eRumOverride();
  if (initialized || (!e2e && !isDatadogRumEnabled(config))) {
    return;
  }

  const applicationId =
    e2e?.applicationId ??
    config.getOptionalString('homestead.datadog.rum.applicationId');
  const clientToken =
    e2e?.clientToken ??
    config.getOptionalString('homestead.datadog.rum.clientToken');
  const orgId =
    e2e?.orgId ?? config.getOptionalString('homestead.datadog.orgId');
  const team =
    e2e?.team ?? config.getOptionalString('homestead.datadog.team');
  // Warn instead of throw so a missing atlas secret does not break the app shell.
  if (!applicationId || !clientToken) {
    // eslint-disable-next-line no-console
    console.warn(
      'Datadog RUM is enabled but homestead.datadog.rum.applicationId or clientToken is missing',
    );
    return;
  }
  if (!orgId || !team) {
    // eslint-disable-next-line no-console
    console.warn(
      'Datadog RUM is enabled but homestead.datadog.orgId or team is missing',
    );
    return;
  }

  const allowedTracingUrls = e2e ? undefined : resolveAllowedTracingUrls(config);

  datadogRum.init({
    applicationId,
    clientToken,
    site: config.getOptionalString('homestead.datadog.rum.site') ?? 'datadoghq.com',
    service: DATADOG_RUM_SERVICE,
    env: resolveDatadogRumEnv(config),
    version: resolveDatadogVersion(config),
    sessionSampleRate:
      config.getOptionalNumber('homestead.datadog.rum.sessionSampleRate') ?? 100,
    sessionReplaySampleRate:
      config.getOptionalNumber('homestead.datadog.rum.sessionReplaySampleRate') ?? 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    ...(allowedTracingUrls ? { allowedTracingUrls } : {}),
  });

  // CFA tag policy: org_id and team are not init() params — use global context.
  datadogRum.setGlobalContextProperty('org_id', orgId);
  datadogRum.setGlobalContextProperty('team', team);

  initialized = true;
}

export function DatadogRumInitializer({ children }: { children: ReactNode }) {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  // Init as early as config is ready; separate from setUser because identity resolves async.
  useEffect(() => {
    initDatadogRum(configApi);
  }, [configApi]);

  useEffect(() => {
    if (!isDatadogRumEnabled(configApi)) {
      return;
    }

    identityApi
      .getBackstageIdentity()
      .then(identity => {
        datadogRum.setUser({ id: identity.userEntityRef });
      })
      .catch(() => {
        // Guest or signed-out sessions are tracked without a user id.
      });
  }, [configApi, identityApi]);

  return <>{children}</>;
}
